import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, AutoComplete, Col, Form, Row, Input, Select, Switch, InputNumber, notification } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import cn from "classnames";
import { ReactComponent as SaveSVG } from "@/assets/icons/feather-icons/save.svg";
import { UserContext } from "@/shared/contexts/user.context";
import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { AssessmentContext } from "@/shared/contexts/assessment.context";
import { Loading } from "@/shared/components/Loading/Loading";
import { isValidNumericParam } from "@/shared/utils/common.utils";
import TableHeader from "@/shared/components/TableHeader/TableHeader";
import { SubmitStatuses } from "@/shared/components/testManagement/TestElementInfoBlock";
import useAllTextLocations from "@/shared/components/assessment/AboutAssessment/hooks/useAllTextLocations";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import {
  IAssessmentData,
  ITextLocation,
  INewAssessmentValues,
} from "@/modules/assessments/scenes/CreateAssessment/assessment.interface";
import { TextLocations } from "@/modules/assessments/scenes/CreateAssessment/createAssessment.constants";
import {
  ALL_SYSTEM_LOCALES,
  DEFAULT_LOCALE,
  localeLabel,
  orderLocales,
  resolveEffectiveLocales,
} from "@/shared/utils/locale.utils";
import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";
import { FeedbackConfigResponse, FeedbackQuestionConfig } from "@/modules/candidate/feedback/candidate-feedback.types";
import { useGetClient } from "@/modules/client/hooks/useGetClient";
import LocaleTextTabs from "./LocaleTextTabs";
import LanguageChips from "./LanguageChips";
import LanguagesField from "./LanguagesField";
import EditingLocaleSwitcher, { LOCALE_SCOPED_FIELDS } from "./EditingLocaleSwitcher";
import LocalizedTitleField from "./LocalizedTitleField";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { CustomInput, InputType } from "@/ui-components/commonComponents/Input/Input";
import { UploadPhoto } from "@/ui-components/commonComponents/UploadPhoto/UploadPhoto";
import { UploadFolder } from "@/ui-components/commonComponents/UploadPhoto/UploadPhoto";
import {
  ASSESSMENT_ROUTE,
  CLIENT_ROUTE,
  NEW_ROUTE,
  NOT_FOUND_ROUTE,
  TESTS_ROUTE,
} from "@/routing/AppRouter/routes.constants";
import { useAllClientJobTitles } from "./hooks/useAllClientJobTitles";
import { useAssessmentTemplatesHook } from "./hooks/useAssessmentTemplates.hook";
import {
  emptyToNull,
  getInitialAssessmentValues,
  hasAnyLocaleContent,
  pickLocaleBody,
  pickLocaleTitles,
} from "./utils";
import tableStyle from "@/modules/tables/style.module.css";
import "./index.css";
import style from "./style.module.css";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";

export enum AssessmentActions {
  NEW = "NEW",
  EDIT = "EDIT",
}

export interface IAboutAssessmentProps {
  type: AssessmentActions;
  isChangeable?: boolean;
  title?: string;
  defaultImageUrl?: string;
  texts?: any;
}

export const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const CLIENT_JOB_TITLE_MAX_LENGTH = 256;

const TEXT_LOCATION_ORDER = new Map<TextLocations, number>([
  [TextLocations.introText, 0],
  [TextLocations.landingText, 1],
  [TextLocations.outroText, 2],
]);

const NO_SHARED_LANGUAGE_MESSAGE =
  "The tests in this template don't share a common language. Please add new languages to this template's tests to create parity, or choose a different template.";

function getOpenEndedDefaultInitialValue(
  questions: FeedbackQuestionConfig[],
  hasExistingConfig: boolean,
): {} | { openEndedEnabled: boolean } {
  let hasOpenEndedQuestion = false;
  const hasOpenEndedEnabled = questions.some((q) => {
    if (q.type === "open_ended") {
      hasOpenEndedQuestion = true;
      return q.enabled;
    }
    return false;
  });

  if (!hasOpenEndedQuestion) {
    return {};
  } else if (!hasExistingConfig) {
    // Encourage sign-up for assessments that have never had feedback enabled
    return { openEndedEnabled: true };
  }

  return { openEndedEnabled: hasOpenEndedEnabled };
}

export function AboutAssessment({ type, isChangeable = true }: IAboutAssessmentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { client_id: clientId } = useParams<{ client_id: string }>();
  const { user } = useContext(UserContext);
  const { assessment, setAssessment } = useContext(AssessmentContext);
  const { client } = useGetClient(Number(clientId));
  const { textLocations, isLoading: textLocationsLoading } = useAllTextLocations();
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<SubmitStatuses>(SubmitStatuses.initial);
  const [feedbackConfig, setFeedbackConfig] = useState<FeedbackConfigResponse | null>(null);
  const [form] = Form.useForm();
  const localeRevalidateMounted = useRef(false);

  const watchedLocales = Form.useWatch("enabledLocales", form) as string[] | undefined;
  const selectedLocales = resolveEffectiveLocales(watchedLocales, assessment?.enabledLocales);
  const [editingLocale, setEditingLocale] = useState<string>(selectedLocales[0]);
  const hasEditableLocales = selectedLocales.length > 1;
  const { clientJobTitles } = useAllClientJobTitles({ clientId: Number(clientId), locale: editingLocale });

  useEffect(() => {
    if (!selectedLocales.includes(editingLocale)) {
      setEditingLocale(selectedLocales[0]);
    }
  }, [selectedLocales, editingLocale]);
  const isLocaleSelectionLocked = type === AssessmentActions.EDIT;

  const searchParams = new URLSearchParams(location.search);
  const isTemplateMode = searchParams.get("template") === "true" && type === AssessmentActions.NEW;
  const isTemplateBased = isTemplateMode || Boolean(assessment?.template);
  const { templates: assessmentTemplates, isLoading: templatesLoading } = useAssessmentTemplatesHook(
    isTemplateMode ? Number(clientId) : undefined,
  );

  // Template tests are fixed, so only locales every preset test supports may be enabled; others allow any.
  const watchedTemplateId = Form.useWatch("templateId", form) as number | undefined;
  const selectedTemplateOption = assessmentTemplates.find((option) => option.value === watchedTemplateId);
  const templateSupportedLocales = isTemplateMode ? selectedTemplateOption?.supportedLocales : undefined;
  const localeOptions: string[] = templateSupportedLocales ?? ALL_SYSTEM_LOCALES;
  const lockedLocale = localeOptions.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : undefined;

  useEffect(() => {
    if (isLocaleSelectionLocked || !lockedLocale || !watchedLocales || watchedLocales.includes(lockedLocale)) {
      return;
    }
    form.setFieldsValue({ enabledLocales: orderLocales([...watchedLocales, lockedLocale]) });
  }, [watchedLocales, lockedLocale, isLocaleSelectionLocked, form]);

  const handleTemplateChange = (templateId: number) => {
    const selectedTemplate = assessmentTemplates.find((template) => template.value === templateId);

    if (!selectedTemplate) return;

    // A template whose tests share no common language can't be used: bounce the selection with a
    // notification so the form never holds an invalid template — the admin must pick another. antd
    // commits the Select's value during this onChange, so clear it on the next tick to override it.
    if (Array.isArray(selectedTemplate.supportedLocales) && selectedTemplate.supportedLocales.length === 0) {
      notification.error({ message: NO_SHARED_LANGUAGE_MESSAGE, duration: 8 });
      setTimeout(() => form.resetFields(["templateId"]), 0);
      return;
    }

    form.setFieldsValue({
      landingPhoto: selectedTemplate.landingImageUrl || "",
      [TextLocations.landingText]: selectedTemplate.landingText ?? {},
    });

    // Drop any enabled locales the template's tests don't support; keep at least one.
    const supported = selectedTemplate.supportedLocales ?? ALL_SYSTEM_LOCALES;
    const currentLocales = (form.getFieldValue("enabledLocales") as string[] | undefined) ?? [];
    const retained = currentLocales.filter((locale) => supported.includes(locale));
    const fallback = supported.includes(DEFAULT_LOCALE) ? [DEFAULT_LOCALE] : supported.slice(0, 1);
    const nextLocales = retained.length ? retained : fallback;
    form.setFieldsValue({ enabledLocales: nextLocales });

    if (selectedTemplate.landingImageUrl) {
      form.validateFields(nextLocales.map((locale) => [TextLocations.landingText, locale]));
    }
  };

  const getAlert = (status: SubmitStatuses) => {
    switch (status) {
      case SubmitStatuses.error:
        return <Alert message={"Something went wrong. Please, retry later."} type={"error"} showIcon />;
      case SubmitStatuses.success:
        return <Alert message={"Success"} type={"success"} showIcon />;
      case SubmitStatuses.initial:
      default:
        return;
    }
  };

  const onFinish = async (values: INewAssessmentValues) => {
    if (!user || !user.userId) {
      // eslint-disable-next-line no-console
      console.log(`Have not found user id`);
      return;
    }

    const localesToSave = resolveEffectiveLocales(values.enabledLocales);

    let assessmentBody: IAssessmentData = {
      userId: user.userId,
      name: pickLocaleTitles(values.name, localesToSave),
      description: values.description,
      clientJobTitle: emptyToNull(pickLocaleTitles(values.clientJobTitle, localesToSave)),
      titleImageUrl: values.photo ?? "",
      texts: [
        {
          locationName: TextLocations.introText,
          body: pickLocaleBody(values[TextLocations.introText], localesToSave),
        },
        ...(isTemplateBased
          ? [
              {
                locationName: TextLocations.landingText,
                body: pickLocaleBody(values[TextLocations.landingText], localesToSave),
              },
            ]
          : []),
        {
          locationName: TextLocations.outroText,
          body: pickLocaleBody(values[TextLocations.outroText], localesToSave),
        },
      ],
      showScaleDevelopment: values.showScaleDevelopment ?? false,
      showInterviewGuide: values.showInterviewGuide ?? false,
    };

    if (type === AssessmentActions.NEW) {
      assessmentBody.enabledLocales = localesToSave;
    }

    if (isTemplateBased) {
      assessmentBody.landingImageUrl = values.landingPhoto ?? "";
    }

    if (values.enableStartReminder === false) {
      assessmentBody.startReminderDays = null;
    } else if (values.startReminderDays) {
      assessmentBody.startReminderDays = values.startReminderDays;
    }

    if (values.enableFinishReminder === false) {
      assessmentBody.finishReminderDays = null;
    } else if (values.finishReminderDays) {
      assessmentBody.finishReminderDays = values.finishReminderDays;
    }

    if (isTemplateMode && values.templateId) {
      assessmentBody.templateId = values.templateId;
    }

    assessmentBody.demographicsEnabled = values.demographicsEnabled ?? false;
    assessmentBody.employeeIdEnabled = values.employeeIdEnabled ?? false;
    assessmentBody.resultValidityDays = values.resultValidityDays ?? 365;

    try {
      setIsLoading(true);

      if (type === AssessmentActions.NEW) {
        // Include feedback config in the create request using available questions
        // Only include questions when feedback is enabled
        assessmentBody.feedbackConfig = {
          feedbackEnabled: values.feedbackEnabled ?? true,
          questions:
            values.feedbackEnabled ?? true
              ? feedbackConfig?.questions?.map((q) => ({
                  questionId: q.questionId,
                  enabled: q.type === "open_ended" ? values.openEndedEnabled ?? true : q.enabled,
                }))
              : undefined,
        };

        const { data } = await AssessmentHttpService.createNewAssessment(Number(clientId), assessmentBody);

        navigate(`/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}/${data.assessmentId}/${TESTS_ROUTE}`);
      }

      if (type === AssessmentActions.EDIT && assessment) {
        // Include feedback config in the main PATCH request
        // Only include questions when feedback is enabled
        assessmentBody.feedbackConfig = {
          feedbackEnabled: values.feedbackEnabled,
          questions: values.feedbackEnabled
            ? feedbackConfig?.questions?.map((q) => ({
                questionId: q.questionId,
                enabled: q.type === "open_ended" ? values.openEndedEnabled ?? true : q.enabled,
              }))
            : undefined,
        };

        const { data } = await AssessmentHttpService.updateAssessment(
          Number(clientId),
          assessment.assessmentId,
          assessmentBody,
        );

        // Update local feedback config state with new values
        // Only update question states when feedback is enabled
        if (feedbackConfig) {
          setFeedbackConfig({
            ...feedbackConfig,
            feedbackEnabled: values.feedbackEnabled ?? feedbackConfig.feedbackEnabled,
            questions: values.feedbackEnabled
              ? feedbackConfig.questions.map((q) => ({
                  ...q,
                  enabled: q.type === "open_ended" ? values.openEndedEnabled ?? true : q.enabled,
                }))
              : feedbackConfig.questions,
          });
        }

        if (setAssessment) {
          setAssessment({ ...assessment, ...data });
        }
        setAlert(SubmitStatuses.success);
      }
    } catch (e: any) {
      console.error(`patch assessment error: ${e}`);
      setAlert(SubmitStatuses.error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isValidNumericParam(clientId)) {
      navigate(NOT_FOUND_ROUTE);
    }
  }, [clientId, navigate]);

  // Feedback settings
  useEffect(() => {
    const fetchFeedbackConfig = async () => {
      if (type === AssessmentActions.EDIT && assessment?.assessmentId) {
        try {
          const config = await AssessmentHttpService.getFeedbackConfig(assessment.assessmentId);
          let questions = config.questions;
          const hasExistingConfig = questions.length > 0;

          if (!hasExistingConfig) {
            const available = await AssessmentHttpService.getAvailableFeedbackQuestions();
            questions = available.questions;
          }

          setFeedbackConfig({
            assessmentId: assessment.assessmentId,
            feedbackEnabled: config.feedbackEnabled,
            questions,
          });

          const openEndedDefaultInitialValue = getOpenEndedDefaultInitialValue(questions, hasExistingConfig);
          form.setFieldsValue({
            feedbackEnabled: config.feedbackEnabled,
            ...openEndedDefaultInitialValue,
          });
        } catch (error) {
          console.error("Failed to fetch feedback config:", error);
        }
      } else if (type === AssessmentActions.NEW) {
        try {
          const config = await AssessmentHttpService.getAvailableFeedbackQuestions();
          setFeedbackConfig({
            assessmentId: 0,
            feedbackEnabled: false,
            questions: config.questions,
          });

          const openEndedDefaultInitialValue = getOpenEndedDefaultInitialValue(config.questions, false);
          form.setFieldsValue({
            feedbackEnabled: true,
            ...openEndedDefaultInitialValue,
          });
        } catch (error) {
          console.error("Failed to fetch available feedback questions:", error);
        }
      }
    };

    fetchFeedbackConfig();
  }, [type, assessment?.assessmentId, form]);

  useEffect(() => {
    if (type !== AssessmentActions.NEW || !client || !textLocations?.length || !client.clientTexts?.length) return;

    textLocations.forEach(({ locationId, name }) => {
      const clientText = client.clientTexts?.find((clientText) => clientText.locationId === locationId);
      if (!clientText?.body) return;

      // Don't overwrite text the admin already typed — the client query can resolve after edits.
      const current = form.getFieldValue(name) as Record<string, string> | undefined;
      if (hasAnyLocaleContent(current, ALL_SYSTEM_LOCALES)) return;

      form.setFieldsValue({ [name]: clientText.body });
    });
  }, [client, textLocations, form, type]);

  const watchedJobTitles = Form.useWatch("clientJobTitle", form) as Record<string, string> | undefined;
  const onInput = (value: string) => {
    const trimmedValue: string = value.slice(0, CLIENT_JOB_TITLE_MAX_LENGTH);
    form.setFields([{ name: ["clientJobTitle", editingLocale], value: trimmedValue }]);
  };

  const clientAbility = useContext(ClientAbilityContext);
  const canCreateAssessment = clientAbility.can(Action.Create, Subject.ASSESSMENT);
  const canUpdateAssessment = clientAbility.can(Action.Update, Subject.ASSESSMENT);

  useRouteAccessController({
    slug: NEW_ROUTE,
    hasPermission: canCreateAssessment,
  });

  const sortedAvailableTextLocations = useMemo(() => {
    let filtered = textLocations;
    if (!isTemplateBased) {
      filtered = filtered.filter((textLocation) => textLocation.name !== TextLocations.landingText);
    }
    return filtered.slice().sort((a, b) => {
      const aOrder = TEXT_LOCATION_ORDER.get(a.name) ?? Infinity;
      const bOrder = TEXT_LOCATION_ORDER.get(b.name) ?? Infinity;
      return aOrder - bOrder;
    });
  }, [textLocations, isTemplateBased]);

  // Re-validate text fields when the language set changes so a newly-required language errors immediately.
  const selectedLocalesKey = selectedLocales.join(",");
  useEffect(() => {
    // Skip the first run: validating on mount would flag empty fields before the admin has touched them.
    if (!localeRevalidateMounted.current) {
      localeRevalidateMounted.current = true;
      return;
    }
    const fields = sortedAvailableTextLocations.flatMap((textLocation) =>
      selectedLocales.map((locale) => [textLocation.name, locale]),
    );
    if (fields.length > 0) {
      form.validateFields(fields).catch(() => undefined);
    }
    // Keyed only on the locale-set string on purpose: this should re-validate when the enabled
    // languages change, not when selectedLocales (a new array each render) or
    // sortedAvailableTextLocations (changes on load / template toggle) get new identities — listing
    // those would re-run on unrelated renders and flag fields the admin hasn't touched yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocalesKey]);

  if (
    (!assessment?.name && type === AssessmentActions.EDIT) ||
    textLocationsLoading ||
    (isTemplateMode && templatesLoading)
  ) {
    return <Loading />;
  }

  const canEditFields =
    !assessment?.archivedOn &&
    isChangeable &&
    (type === AssessmentActions.NEW ? canCreateAssessment : canUpdateAssessment);

  const initialValues = getInitialAssessmentValues(textLocations, assessment);

  // All-or-nothing: optional until any language is filled, then required for every enabled language.
  const requiredTextValidator =
    (location: TextLocations, locale: string) => (_: unknown, value: string | undefined) => {
      const body = (form.getFieldValue(location) ?? {}) as Record<string, string>;

      if (hasAnyLocaleContent(body, selectedLocales) && (!value || value.trim() === "")) {
        return Promise.reject(
          new Error(`${location} in ${localeLabel(locale)} is required when it has been provided in another language`),
        );
      }
      return Promise.resolve();
    };

  const landingTextValidator = (locale: string) => (_: unknown, value: string | undefined) => {
    const landingPhoto = form.getFieldValue("landingPhoto");
    if (landingPhoto && (!value || value.trim() === "")) {
      return Promise.reject(
        new Error(`Landing Text in ${localeLabel(locale)} is required when a Landing Image is provided`),
      );
    }
    return Promise.resolve();
  };

  return (
    <div className={tableStyle.tableBlock}>
      <TableHeader tableName="Assessment" hasSearchField={false} handleSearchChange={() => {}} />
      <Form
        form={form}
        name="assessmentMain"
        {...formItemLayout}
        onFinish={onFinish}
        onFinishFailed={() => {
          notification.error({ message: "Please fix the highlighted errors before submitting." });
        }}
        scrollToFirstError={{ block: "center" }}
        initialValues={initialValues}
        requiredMark={false}
      >
        <Row className={style.languagesRow} gutter={30}>
          <Col xs={24} md={hasEditableLocales ? 12 : 24}>
            <div className="inputTitle">
              <label id="enabled-locales-label">Languages Available</label>
              <p className={style.helperText} id="enabled-locales-desc">
                {isLocaleSelectionLocked
                  ? "This assessment is available in the following languages"
                  : "Select the languages available for this assessment"}
              </p>
              <Form.Item
                name="enabledLocales"
                rules={
                  isLocaleSelectionLocked
                    ? []
                    : [{ required: true, type: "array", min: 1, message: "Select at least one language" }]
                }
              >
                {isLocaleSelectionLocked ? (
                  <LanguageChips aria-labelledby="enabled-locales-label" aria-describedby="enabled-locales-desc" />
                ) : (
                  <LanguagesField
                    locales={localeOptions}
                    lockedLocale={lockedLocale}
                    disabled={!canEditFields || (isTemplateMode && !watchedTemplateId)}
                    aria-labelledby="enabled-locales-label"
                    aria-describedby="enabled-locales-desc"
                  />
                )}
              </Form.Item>
            </div>
          </Col>
          {hasEditableLocales && (
            <Col xs={24} md={12}>
              <EditingLocaleSwitcher
                locales={selectedLocales}
                activeLocale={editingLocale}
                onChange={setEditingLocale}
                localizedPaths={[
                  ...LOCALE_SCOPED_FIELDS.map((field) => [field]),
                  ...textLocations.map((item) => [item.name]),
                ]}
              />
            </Col>
          )}
        </Row>

        <Row className={style.rowContainer} gutter={30}>
          <Col xxl={8} xl={8} md={12} xs={24} sm={24}>
            <div className={style.imageContainer}>
              <Form.Item validateFirst className={style.image} name="photo">
                <UploadPhoto
                  onUpload={(url) => form.setFieldsValue({ photo: url })}
                  disabled={!canEditFields}
                  clientID={String(clientId)}
                  defaultImageUrl={initialValues.photo}
                  uploadFolder={UploadFolder.assessmentFolder}
                  uploadText="Add Title Photo"
                  onRemove={() => {
                    form.setFieldsValue({ photo: "" });
                  }}
                  removeText="Remove title photo"
                />
              </Form.Item>
            </div>
          </Col>

          <Col xxl={14} xl={14} md={12} xs={24} sm={24}>
            {isTemplateMode ? (
              <>
                {assessmentTemplates.length === 0 && (
                  <div className={style.templateWarningContainer}>
                    <ExclamationCircleOutlined className={style.templateWarningIcon} />
                    <p className={style.templateWarningText}>No templates are available</p>
                  </div>
                )}
                <Form.Item
                  className="inputTitle"
                  key="templateId"
                  name="templateId"
                  label="Assessment Template"
                  rules={[
                    {
                      required: true,
                      message: "Please select an Assessment Template",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Start typing to select a template..."
                    options={assessmentTemplates}
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      const label = String(option?.label || "");
                      return label.toLowerCase().indexOf(input.toLowerCase()) !== -1;
                    }}
                    disabled={!canEditFields}
                    notFoundContent={assessmentTemplates.length === 0 ? "No templates available" : undefined}
                    className={style.dropdown}
                    onChange={handleTemplateChange}
                  />
                </Form.Item>
              </>
            ) : type === AssessmentActions.EDIT && assessment?.template ? (
              <div className={cn("inputTitle", style.templateContainer)}>
                <span className={style.templateLabel}>Assessment Template</span>
                <p className={style.templateText}>{assessment.template.name}</p>
              </div>
            ) : null}
            <LocalizedTitleField
              name="name"
              locales={selectedLocales}
              activeLocale={editingLocale}
              label="Assessment Name"
              className="inputTitle"
              rulesForLocale={(locale) =>
                locale === DEFAULT_LOCALE
                  ? [{ required: true, whitespace: true, message: "Please Enter Assessment Name" }]
                  : []
              }
            >
              {() => (
                <Input
                  showCount
                  placeholder="Type..."
                  disabled={!canEditFields}
                  maxLength={128}
                  className={style.dropdown}
                />
              )}
            </LocalizedTitleField>
            <LocalizedTitleField
              name="clientJobTitle"
              locales={selectedLocales}
              activeLocale={editingLocale}
              label="Company Job Title"
              className={cn("inputTitle", style.mdEditor)}
              dataLengthForLocale={(locale) =>
                `${(watchedJobTitles?.[locale] ?? "").length} / ${CLIENT_JOB_TITLE_MAX_LENGTH}`
              }
              rulesForLocale={(locale) =>
                locale === DEFAULT_LOCALE
                  ? [{ required: true, whitespace: true, message: "Please Enter Company Job Title" }]
                  : []
              }
            >
              {(locale) => (
                <AutoComplete
                  options={locale === editingLocale ? clientJobTitles?.map((title) => ({ value: title })) : []}
                  placeholder={"Type..."}
                  disabled={!canEditFields}
                  className={style.dropdown}
                  filterOption={(inputValue, option) =>
                    option ? option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1 : false
                  }
                  onChange={onInput}
                ></AutoComplete>
              )}
            </LocalizedTitleField>
            <Form.Item className="inputTitle" key="description" name="description" label="Assessment Description">
              <CustomInput
                className={style.dropdown}
                placeholder="Type..."
                disabled={!canEditFields}
                inputType={InputType.textarea}
                maxLength={1000}
              />
            </Form.Item>
          </Col>
        </Row>

        {sortedAvailableTextLocations.map((item: ITextLocation) => (
          <Row key={item.locationId}>
            <Col span={24}>
              {item.name === TextLocations.landingText ? (
                <>
                  <div className={style.landingImageContainer}>
                    <Form.Item
                      validateFirst
                      className={style.image}
                      shouldUpdate={(prev, curr) => prev.templateId !== curr.templateId}
                    >
                      {() => (
                        <Form.Item name="landingPhoto">
                          <UploadPhoto
                            key={form.getFieldValue("templateId") || "empty"} // It is not enough to just force a re-render; internal state must be reset because UploadPhoto is not a controlled component
                            onUpload={(url) => {
                              form.setFieldsValue({ landingPhoto: url });
                              form.validateFields(selectedLocales.map((locale) => [TextLocations.landingText, locale]));
                            }}
                            disabled={!canEditFields}
                            clientID={String(clientId)}
                            defaultImageUrl={form.getFieldValue("landingPhoto")}
                            uploadFolder={UploadFolder.assessmentFolder}
                            uploadText="Add Landing Photo"
                            onRemove={() => {
                              form.setFieldsValue({ landingPhoto: "" });
                              form.validateFields(selectedLocales.map((locale) => [TextLocations.landingText, locale]));
                            }}
                            removeText="Remove landing photo"
                          />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </div>
                  <LocaleTextTabs
                    location={item.name}
                    locales={selectedLocales}
                    activeLocale={editingLocale}
                    isChangeable={canEditFields}
                    rulesForLocale={(locale) => [{ validator: landingTextValidator(locale) }]}
                  />
                </>
              ) : (
                <LocaleTextTabs
                  location={item.name}
                  locales={selectedLocales}
                  activeLocale={editingLocale}
                  isChangeable={canEditFields}
                  rulesForLocale={(locale) => [{ validator: requiredTextValidator(item.name, locale) }]}
                />
              )}
            </Col>
          </Row>
        ))}

        <Row gutter={[24, 0]} className={style.settingsRow}>
          <Col xs={24} sm={24} md={12}>
            <fieldset className={style.fieldset}>
              <legend>Feedback Settings</legend>
              <div className={style.notificationSettingContainer}>
                <div className={style.switchContainer}>
                  <Form.Item name="feedbackEnabled" valuePropName="checked">
                    <Switch id="feedbackEnabled" disabled={!canEditFields} />
                  </Form.Item>
                  <div>
                    <label htmlFor="feedbackEnabled">Collect candidate feedback</label>
                  </div>
                </div>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.feedbackEnabled !== curr.feedbackEnabled}>
                  {(props) => {
                    if (props.getFieldValue("feedbackEnabled") !== true) return null;
                    const openEndedQuestionCount =
                      feedbackConfig?.questions?.filter((q) => q.type === "open_ended").length ?? 0;
                    if (openEndedQuestionCount === 0) return null;
                    return (
                      <div className={style.nestedSwitchContainer}>
                        <Form.Item name="openEndedEnabled" valuePropName="checked">
                          <Switch id="openEndedEnabled" disabled={!canEditFields} />
                        </Form.Item>
                        <div>
                          <label htmlFor="openEndedEnabled">
                            Include open-ended {openEndedQuestionCount === 1 ? "question" : "questions"} at end of
                            survey
                          </label>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>
              </div>
            </fieldset>

            <fieldset className={style.fieldset}>
              <legend>Notification Settings</legend>
              <div className={style.notificationSettingContainer}>
                <div className={style.switchContainer}>
                  <Form.Item name="enableStartReminder" valuePropName="checked">
                    <Switch id="enableStartReminder" disabled={!canEditFields} />
                  </Form.Item>
                  <div>
                    <label htmlFor="enableStartReminder">Email reminder for candidates to start their assessment</label>
                    <Form.Item
                      noStyle={true}
                      shouldUpdate={(prev, curr) => prev.enableStartReminder !== curr.enableStartReminder}
                    >
                      {(props) => {
                        if (props.getFieldValue("enableStartReminder") !== true) return null;
                        return (
                          <div className={style.daysContainer}>
                            <p aria-hidden="true">Send reminder after</p>
                            <Form.Item
                              name="startReminderDays"
                              rules={[
                                { required: true, message: "Required" },
                                { type: "number", min: 1 },
                                { pattern: /^\d+$/, message: "Whole numbers only" },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                disabled={!canEditFields}
                                aria-label="Days to wait before sending start reminder"
                              />
                            </Form.Item>
                            <p aria-hidden="true">days</p>
                          </div>
                        );
                      }}
                    </Form.Item>
                  </div>
                </div>

                <div className={style.switchContainer}>
                  <Form.Item name="enableFinishReminder" valuePropName="checked">
                    <Switch id="enableFinishReminder" disabled={!canEditFields} />
                  </Form.Item>
                  <div>
                    <label htmlFor="enableFinishReminder">
                      Email reminder for candidates to finish their assessment
                    </label>
                    <Form.Item
                      noStyle={true}
                      shouldUpdate={(prev, curr) => prev.enableFinishReminder !== curr.enableFinishReminder}
                    >
                      {(props) => {
                        if (props.getFieldValue("enableFinishReminder") !== true) return null;
                        return (
                          <div className={style.daysContainer}>
                            <p aria-hidden="true">Send reminder after</p>
                            <Form.Item
                              name="finishReminderDays"
                              rules={[
                                { required: true, message: "Required" },
                                { type: "number", min: 1 },
                                { pattern: /^\d+$/, message: "Whole numbers only" },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                disabled={!canEditFields}
                                aria-label="Days to wait before sending finish reminder"
                              />
                            </Form.Item>
                            <p aria-hidden="true">days</p>
                          </div>
                        );
                      }}
                    </Form.Item>
                  </div>
                </div>
              </div>
            </fieldset>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <fieldset className={style.fieldset}>
              <legend>Demographic Data Settings</legend>
              <div className={style.notificationSettingContainer}>
                <div className={style.switchContainer}>
                  <Form.Item name="demographicsEnabled" valuePropName="checked">
                    <Switch id="demographicsEnabled" disabled={!canEditFields} />
                  </Form.Item>
                  <div>
                    <label htmlFor="demographicsEnabled">Collect candidate demographic details</label>
                  </div>
                </div>
                <div className={style.switchContainer}>
                  <Form.Item name="employeeIdEnabled" valuePropName="checked">
                    <Switch id="employeeIdEnabled" disabled={!canEditFields} />
                  </Form.Item>
                  <div>
                    <label htmlFor="employeeIdEnabled">Employee ID field</label>
                    <p className={style.helperText}>
                      This is a required field. Please only use this if you are running a validation study.
                    </p>
                  </div>
                </div>
              </div>
            </fieldset>

            <fieldset className={style.fieldset}>
              <legend>Assessment Results Expiration Period</legend>
              <div className={style.notificationSettingContainer}>
                <div className={style.expirationDaysContainer}>
                  <p aria-hidden="true">Results expire after</p>
                  <Form.Item
                    name="resultValidityDays"
                    rules={[
                      { required: true, message: "Required" },
                      {
                        validator: (_: any, value: number | string | undefined | null) => {
                          if (value === undefined || value === null || value === "") {
                            return Promise.resolve();
                          }
                          const num = Number(value);
                          if (!Number.isInteger(num) || num < 1) {
                            return Promise.reject(new Error("Please enter a whole number"));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      disabled={!canEditFields}
                      aria-label="Result validity period in days"
                    />
                  </Form.Item>
                  <p aria-hidden="true">days</p>
                </div>
              </div>
            </fieldset>
          </Col>
        </Row>

        {isTemplateBased && (
          <Row>
            <Col span={24}>
              <fieldset className={style.fieldset}>
                <legend>Report Settings</legend>
                <div className={style.notificationSettingContainer}>
                  <div className={style.switchContainer}>
                    <Form.Item name="showScaleDevelopment" valuePropName="checked" initialValue={true}>
                      <Switch id="showScaleDevelopment" disabled={!canEditFields} defaultChecked />
                    </Form.Item>
                    <div>
                      <label htmlFor="showScaleDevelopment">Show develop report</label>
                    </div>
                  </div>
                  <div className={style.switchContainer}>
                    <Form.Item name="showInterviewGuide" valuePropName="checked" initialValue={true}>
                      <Switch id="showInterviewGuide" disabled={!canEditFields} defaultChecked />
                    </Form.Item>
                    <div>
                      <label htmlFor="showInterviewGuide">Show interview guide</label>
                    </div>
                  </div>
                </div>
              </fieldset>
            </Col>
          </Row>
        )}

        <Row>
          <Col xxl={18} xl={18} lg={24} md={24} sm={24} xs={24}>
            {type === AssessmentActions.NEW && (
              <div className={style.buttonWithAlertContainer}>
                <SubmitButton arrowAfter isLoading={isLoading} disabled={!canEditFields}>
                  Create
                </SubmitButton>
                <div className={style.alertContainer}>{getAlert(alert)}</div>
              </div>
            )}
            {type === AssessmentActions.EDIT && (
              <div className={style.buttonWithAlertContainer}>
                <SubmitButton icon={<SaveSVG aria-hidden="true" />} isLoading={isLoading} disabled={!canEditFields}>
                  Save
                </SubmitButton>
                <div className={style.alertContainer}>{getAlert(alert)}</div>
              </div>
            )}
          </Col>
        </Row>
      </Form>
    </div>
  );
}
